// @ts-check
/**
 * @typedef {import('../TypeTester.js').TypeTester} TypeTester
 * @typedef {import('../text-editor/TextEditor.js').TextEditor} TextEditor
 */

import { isBlank } from "common/utils/blank.js";
import debug from "debug";

/**
 * Cuts the text length so that it does not get unreasonably long.
 *
 * Some samples were designed with the max-height in mind, so they are very long, but max-height does not work properly in Safari.
 * Instead of shortening the samples manually, we can use this plugin to do it automatically.
 *
 * #safarimaxheightissue_option6
 */
export class TextLengthLimiterPlugin {
  isEnabled = false;

  quill = null;

  // This is set when the the user checks the checkbox on the admin
  forceTextShortenerMaxHeightStrategy = false;

  /** @type {null | number} */
  initialTextLength = null;

  /**
   * @param {TypeTester} typeTester
   */
  constructor(typeTester) {
    this.typeTester = typeTester;

    this.log = debug("type-tester:text-length-limiter-plugin");

    typeTester.addEventListener(
      "text-editor-instantiated",
      ({ detail: { textEditor } }) => this.onEditorInstantiated(textEditor)
    );
  }

  // Called from applySampleFormatting.js
  //
  setEnabled(enabled) {
    if (this.isEnabled === enabled) {
      this.log(`already ${enabled ? `enabled` : `disabled`}, skipping`);
      // Calling limitTextLength will reset the contents of the editor, which will make it reset the cursor position,
      // therefore we should only do it if the state is actually changing.
      return;
    }
    this.log(`${enabled ? `enable` : `disable`}`);
    this.isEnabled = enabled;
    if (this.quill) {
      // Even if disabling - we need to call this to restore the original text
      this.limitTextLength(this.quill);
    }
  }

  enable() {
    this.setEnabled(true);
  }

  disable() {
    this.setEnabled(false);
  }

  setInitialTextLength(initialTextLength) {
    this.log(`setInitialTextLength: ${initialTextLength}`);
    this.initialTextLength = initialTextLength;
    // If the text length is set, we need to recalculate the character limit
    if (this.quill) {
      this.limitTextLength(this.quill);
    }
  }

  /**
   * @param {TextEditor} textEditor
   */
  onEditorInstantiated(textEditor) {
    textEditor.addEventListener(
      "quill-editor-initialized",
      ({ detail: { quill } }) => {
        this.log(`quill-editor-initialized`, quill);
        this.quill = quill;
        this.limitTextLength(quill);

        textEditor.addEventListener("change", (event) => {
          // We should do any shortening while the user is typing, it will cause the editor to unfocus.
          if (event.detail.reason !== "text-change") {
            // this.log(`>>> change`, event.detail.reason);
            this.limitTextLength(quill);
          }
        });

        // applySampleFormatting sends events when it recalculates the style
        textEditor.element.addEventListener("useTextShortening", (event) => {
          /** @type {boolean} */
          // @ts-ignore
          const useTextShortening = event.detail.useTextShortening;
          this.log(`useTextShortening: ${useTextShortening}`);
          if (useTextShortening && !this.isEnabled) {
            this.log(`enabling text length limiter`);
            this.enable();
          } else if (!useTextShortening && this.isEnabled) {
            this.log(`disabling text length limiter`);
            this.disable();
          }
        });
      }
    );
  }

  /**
   * @param {*} quill
   */
  async limitTextLength(quill) {
    const { textEditor } = this.typeTester;

    let characterLimit = this.calculateCharacterLimit();
    this.log(`characterLimit: ${characterLimit}`);

    const shadowQuill = textEditor.shadowQuill;
    if (!shadowQuill) {
      this.log(`shadowQuill not found, skipping`);
      return;
    }

    // First, reapply all the full content to the editor so that we can get the text
    quill.setContents(
      JSON.parse(JSON.stringify(shadowQuill.getContents())),
      "silent"
    );

    const text = shadowQuill.getText();

    // Find the first space after the character limit
    if (characterLimit !== null) {
      const spaceIndex = indexOfRegex(text, /[ ,;\n]/, characterLimit);
      if (spaceIndex !== -1 && spaceIndex < characterLimit + 20) {
        characterLimit = spaceIndex;
      }
    }

    // Find the first period or sentence-end punctuation after the character limit with regex
    // const punctuationIndex = text.substring(characterLimit).search(/[.!?]/);
    // if (punctuationIndex !== -1 && punctuationIndex < characterLimit + 40) {
    //   characterLimit += punctuationIndex + 1;
    // }

    if (characterLimit === null) {
      this.log(`text-length-limiter: characterLimit is null, skipping`);
      return;
    }
    if (characterLimit < 0) {
      console.warn(`text-length-limiter: characterLimit is negative, skipping`);
      return;
    }

    // Note: The second argument is the length of the text to delete, but there is a bug that results in the whole text being deleted, which does not seem to occur when using a larger number.
    quill.deleteText(characterLimit, quill.getLength(), "silent");
  }

  /** @type {() => number | null} */
  calculateCharacterLimit() {
    if (!this.isEnabled) {
      this.log(
        `inactive (!isEnabled) - returning null from calculateCharacterLimit`
      );
      return null;
    }

    /** @type number | undefined | null */
    const maxHeight = this.typeTester.props.options.maxHeight;

    if (isBlank(maxHeight)) {
      this.log(`maxHeight is blank, skipping`);
      return null;
    }

    const { textEditor } = this.typeTester;
    const { fontSize, lineHeight, letterSpacing } = textEditor.mainFormat;
    const { charWidth } = textEditor.fontStyle;
    const initialFontSize = this.typeTester.props.format.fontSize;
    const initialLineHeight = this.typeTester.props.format.lineHeight;
    const initialLetterSpacing = this.typeTester.props.format.letterSpacing;
    const initialTextLength =
      this.initialTextLength ?? this.typeTester.props.options.initialTextLength;

    if (initialTextLength !== null) {
      // The admin has set a text length to be applied when the sample is initially rendered.
      // If the user changes the font size or line height, we need to calculate the character limit proportionally.
      const ratio =
        calculateCharacterArea(fontSize, lineHeight, charWidth, letterSpacing) /
        calculateCharacterArea(
          initialFontSize,
          initialLineHeight,
          charWidth,
          initialLetterSpacing
        );
      const characterLimit = Math.round(initialTextLength / ratio);
      return characterLimit;
    } else {
      const characterHeight = (fontSize * lineHeight) / 100;

      const characterArea = calculateCharacterArea(
        fontSize,
        lineHeight,
        charWidth,
        letterSpacing
      );
      if (characterArea < 1) {
        console.warn(
          `text-length-limiter: characterArea is too small, skipping`
        );
        return null;
      }

      const containerWidth = 1000; // font size is set for 1000px, the actual font size is scaled according to the screen width; therefor for this calculation the width is irrelevant

      const containerArea = containerWidth * maxHeight;

      const magicNumber = 1.2; // The lower, the longer the text.

      const characterLimit = Math.ceil(
        containerArea / characterArea / magicNumber
      );

      this.log(
        `characterLimit: ${characterLimit}\n` +
          `  characterHeight: ${characterHeight}\n` +
          `  characterArea: ${characterArea}\n` +
          `  containerWidth: ${containerWidth}\n` +
          `  containerArea: ${containerArea}\n` +
          ``
      );

      return characterLimit;
    }
  }
}

const calculateCharacterArea = (
  fontSize,
  lineHeight,
  charWidth,
  letterSpacing
) => {
  const characterHeight = (fontSize * lineHeight) / 100;

  const characterWidthRatioToFontSize = charWidth / 100;
  const letterSpacingPxPerEm = letterSpacing / 100; // Letter-spacing is defined as 0.01em, ie. 1/100th of the font size
  const characterWidth =
    fontSize * characterWidthRatioToFontSize + fontSize * letterSpacingPxPerEm;

  // fontSize**2 * lineHeight * (charWidth + letterSpacing) / 100 / 100
  return characterHeight * characterWidth;
};

const indexOfRegex = (text, regex, fromIndex = 0) => {
  const match = text.substring(fromIndex).match(regex);
  if (!match) return -1;
  return match.index + fromIndex;
};
